// The affiliate program (/affiliates): one payload query, the nav badge, and
// the mutations. Every write invalidates the payload. Approving an
// application is the only call with a side effect outside our database (it
// creates the affiliate in Rewardful); nothing here moves money.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import type {
  AffiliateProspectInput, AffiliatesPayload, DecideAffiliateRequest,
} from '@shared/types/affiliates';

export const AFFILIATES_KEY = ['admin', 'affiliates', 'program'];
const BADGE_KEY = ['admin', 'affiliates', 'badge'];

export function useAffiliates() {
  return useQuery({
    queryKey: AFFILIATES_KEY,
    queryFn: async () => {
      const res = await api.get<AffiliatesPayload>('/admin/affiliates/program');
      if (!res.success || !res.data) throw new Error(res.error || 'Failed to load');
      return res.data;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/** Applications waiting on a decision, polled for the nav badge. */
export function useAffiliatePendingCount(enabled: boolean) {
  const q = useQuery({
    queryKey: BADGE_KEY,
    enabled,
    queryFn: async () => {
      const res = await api.get<{ pendingCount: number }>('/admin/affiliates/program/badge');
      return res.data?.pendingCount ?? 0;
    },
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
    refetchOnWindowFocus: true,
  });
  return q.data ?? 0;
}

export function useAffiliateMutations() {
  const qc = useQueryClient();
  const done = () => {
    void qc.invalidateQueries({ queryKey: AFFILIATES_KEY });
    void qc.invalidateQueries({ queryKey: BADGE_KEY });
    // The inbox labels threads by track; a new affiliate changes that.
    void qc.invalidateQueries({ queryKey: ['admin', 'creators', 'inbox'] });
  };

  const decide = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & DecideAffiliateRequest) =>
      api.post<{ status: string; rewardfulError: string | null }>(`/admin/affiliates/applications/${id}/decide`, body),
    onSuccess: done,
  });

  const importProspects = useMutation({
    mutationFn: (body: { prospects: AffiliateProspectInput[] }) =>
      api.post<{ inserted: number; skipped: number }>('/admin/affiliates/prospects/import', body),
    onSuccess: done,
  });

  const updateProspect = useMutation({
    mutationFn: ({ id, ...body }: { id: string; status?: string; notes?: string | null; email?: string | null }) =>
      api.patch(`/admin/affiliates/prospects/${id}`, body),
    onSuccess: done,
  });

  const sync = useMutation({
    mutationFn: () => api.post<{ affiliates: number; matched: number; created: number; error: string | null; configured: boolean }>('/admin/affiliates/sync', {}),
    onSuccess: done,
  });

  return { decide, importProspects, updateProspect, sync };
}
